export function resolveTenantProfile(tenant) {
  const explicit = String(tenant?.tenant_profile || '').trim().toLowerCase();
  if (explicit === 'consultoria' || explicit === 'orgao') return explicit;
  return String(tenant?.org_type || '').toUpperCase() === 'COMPANY' ? 'consultoria' : 'orgao';
}

export function isConsultoriaTenant(tenant) {
  return resolveTenantProfile(tenant) === 'consultoria';
}

/** Consultoria fala em cliente. Órgão público fala em requerente. */
export function partyNoun(tenant) {
  const consultoria = isConsultoriaTenant(tenant);
  return {
    consultoria,
    one: consultoria ? 'cliente' : 'requerente',
    many: consultoria ? 'clientes' : 'requerentes',
    One: consultoria ? 'Cliente' : 'Requerente',
    Many: consultoria ? 'Clientes' : 'Requerentes',
  };
}

export const CONSULTORIA_HIDDEN_PATHS = Object.freeze([
  '/institution',
  '/process/inbox',
  '/process/routing',
  '/complaint',
  '/inspections',
]);

export function isConsultoriaHiddenPath(pathname, tenant) {
  if (!isConsultoriaTenant(tenant)) return false;
  const raw = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  return CONSULTORIA_HIDDEN_PATHS.some((prefix) => raw === prefix || raw.startsWith(`${prefix}/`));
}

export const REQUESTER_ROLES = Object.freeze(['empresa', 'empreendimento', 'requerente']);

export function isRequesterRole(user) {
  const roles = (user?.roles || []).map((role) => String(role).toLowerCase());
  return REQUESTER_ROLES.some((role) => roles.includes(role));
}

export function isPersonRequesterRole(user) {
  const roles = (user?.roles || []).map((role) => String(role).toLowerCase());
  return roles.includes('requerente');
}

/** Cadastros e filas internas do órgão — requerente não deve ver no menu nem abrir a tela. */
export const REQUESTER_HIDDEN_PATHS = Object.freeze([
  '/',
  '/license/types',
  '/license/submodules',
  '/licenses/assisted',
  '/users',
  '/settings',
  '/history',
  '/trash',
  '/process/routing',
  '/process/inbox',
  '/me',
]);

/** Pessoa física: sem mapa/relatórios/cadastros internos do órgão. */
export const PERSON_REQUESTER_HIDDEN_PATHS = Object.freeze([
  '/map',
  '/reports',
  '/identity',
  '/process/media-types',
]);

function pathMatchesPrefix(pathname, prefix) {
  const raw = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  return raw === prefix || raw.startsWith(`${prefix}/`);
}

export function isRequesterHiddenPath(pathname, user) {
  if (!isRequesterRole(user)) return false;
  if (REQUESTER_HIDDEN_PATHS.some((prefix) => pathMatchesPrefix(pathname, prefix))) return true;
  if (!isPersonRequesterRole(user)) return false;
  return PERSON_REQUESTER_HIDDEN_PATHS.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

export function isStaffHomeRole(user) {
  const roles = (user?.roles || []).map((role) => String(role).toLowerCase());
  return roles.includes('consultor') || roles.includes('analista');
}

export function isCoordinatorRole(user) {
  const roles = (user?.roles || []).map((role) => String(role).toLowerCase());
  return roles.includes('coordenador') || roles.includes('gestor_ambiental') || roles.includes('tenant_admin');
}

export function isSuperAdminUser(user) {
  return (user?.roles || []).map((role) => String(role).toLowerCase()).includes('super_admin');
}

export function slugFromHostname(hostname, baseDomain) {
  const host = String(hostname || '').split(':')[0].toLowerCase().trim();
  const base = String(baseDomain || '').toLowerCase().trim().replace(/^\./, '');
  if (!host || host === 'localhost' || host === '127.0.0.1') return 'default';
  if (!base) return host.split('.')[0] || 'default';
  if (host === base || host === `www.${base}`) return 'default';
  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return 'default';
  const sub = host.slice(0, -suffix.length);
  if (!sub || sub.startsWith('.')) return 'default';
  return sub.split('.')[0];
}

export function isTenantSubdomain(hostname, baseDomain) {
  return slugFromHostname(hostname, baseDomain) !== 'default';
}

export function tenantMatchesHost(tenant, hostname, baseDomain) {
  const slug = String(tenant?.slug || '').toLowerCase();
  if (!slug) return false;
  return slug === slugFromHostname(hostname, baseDomain);
}

export function userBelongsToTenant(user, tenant) {
  if (!user || !tenant?.id) return true;
  if (isSuperAdminUser(user)) return true;
  const orgId = user.organization_id;
  if (!orgId) return true;
  return String(orgId) === String(tenant.id);
}
